import { useCallback, useMemo } from 'react';

import { AgGridServerTable } from 'core/components';
import { TableLayoutType } from 'core/enums';
import { AgGridServerTableProvider, SelectionCountProvider } from 'core/states';
import { AgGridGetRowsParams, ISelectionActionProps, ITableActionProps } from 'core/types';
import { discountService } from 'corporate/services';
import {
  RemovePricingProfileCostingCalculationsButton,
  UpsertPricingProfileCostingCalculationButton,
} from 'pricing/components';
import { IPricingProfile, IPricingProfileCostingCalculation } from 'pricing/types';

import { getColumnDef } from './columns';

interface IProps {
  pricingProfile: IPricingProfile;
  isReadOnly?: boolean;
}

export const CostingCalculationList = ({ pricingProfile, isReadOnly = false }: IProps) => {
  const defaultColDef = useMemo(
    () => ({
      autoHeight: true,
    }),
    []
  );

  const columnDefs = useMemo(
    () => getColumnDef({ pricingProfile, isEditable: !isReadOnly }),
    [isReadOnly, pricingProfile]
  );

  const actionButtons = useCallback(
    ({ gridApi }: ITableActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <UpsertPricingProfileCostingCalculationButton
          key="addCostingCalculation"
          pricingProfile={pricingProfile}
          onSubmitSuccess={() => gridApi?.refreshServerSide()}
        />,
      ];
    },
    [isReadOnly, pricingProfile]
  );

  const selectionActionButtons = useCallback(
    ({ gridApi, selectedRows, deselectAllRows }: ISelectionActionProps) => {
      if (isReadOnly) {
        return [];
      }

      return [
        <RemovePricingProfileCostingCalculationsButton
          key="removeSelectedCostingCalculations"
          pricingProfileCostingCalculations={selectedRows as IPricingProfileCostingCalculation[]}
          onRemoveSuccess={() => {
            deselectAllRows();
            gridApi?.refreshServerSide();
          }}
        />,
      ];
    },
    [isReadOnly]
  );

  const getRows = useCallback(
    ({ filters, ...rest }: AgGridGetRowsParams) => {
      return discountService.getDiscounts<IPricingProfileCostingCalculation>({
        filters: {
          ...filters,
          pricingProfileId: pricingProfile.id,
          structureId: [pricingProfile.costDiscountStructureId],
        },
        ...rest,
      });
    },
    [pricingProfile.id, pricingProfile.costDiscountStructureId]
  );

  return (
    <SelectionCountProvider>
      <AgGridServerTableProvider
        tableId="pricingProfileCostingCalculationsTable"
        entityType="CostingCalculation"
        defaultColDef={defaultColDef}
        columnDefs={columnDefs}
        getRows={getRows}
        layoutType={TableLayoutType.PRICING_PROFILE_COSTING_CALCULATION}
        hasLayoutActions
        hasManageFilters
      >
        <AgGridServerTable
          actionButtons={actionButtons}
          selectionActionButtons={selectionActionButtons}
        />
      </AgGridServerTableProvider>
    </SelectionCountProvider>
  );
};
